import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WorkspaceService } from '../../../services/workspace.service';
import { PopupService } from '../../../services/popup.service';
import { WORKSPACE_MAX_SIZE_BYTES, WorkspaceFile } from '../../../models/workspace.model';
import { DeleteWorkspaceModalComponent } from '../delete-workspace-modal/delete-workspace-modal.component';

interface WorkspaceData {
  id: string;
  name: string;
  description: string;
  instructions: string;
  files?: WorkspaceFile[];
}

interface DeleteFileState {
  show: boolean;
  file: WorkspaceFile | null;
  isDeleting: boolean;
}

const MB = 1024 * 1024;

@Component({
  selector: 'app-edit-workspace',
  standalone: true,
  imports: [CommonModule, FormsModule, DeleteWorkspaceModalComponent],
  templateUrl: './edit-workspace.component.html',
  styleUrl: './edit-workspace.component.scss'
})
export class EditWorkspaceComponent implements OnInit {
  @Input() workspace: any;
  @Input() saving = false;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<WorkspaceData>();
  @Output() filesUpdated = new EventEmitter<void>(); // Emit when files are changed

  workspaceData: WorkspaceData = {
    id: '',
    name: '',
    description: '',
    instructions: '',
    files: []
  };

  uploadingFile = false;
  loadingFiles = false;

  deleteFileState: DeleteFileState = {
    show: false,
    file: null,
    isDeleting: false
  };

  constructor(
    private workspaceService: WorkspaceService,
    private popupService: PopupService
  ) {}

  ngOnInit() {
    if (this.workspace) {
      this.workspaceData = {
        id: this.workspace.id,
        name: this.workspace.name,
        description: this.workspace.description || '',
        instructions: this.workspace.instructions || '',
        files: []
      };

      this.loadWorkspaceFiles();
    }
  }

  private loadWorkspaceFiles(): void {
    if (this.workspace.id) {
      this.loadingFiles = true;
      this.workspaceService.getWorkspaceFiles(this.workspace.id).subscribe({
        next: (response) => {
          this.workspaceData.files = response.files;
          this.loadingFiles = false;
        },
        error: () => {
          this.loadingFiles = false;
          this.popupService.notify('Failed to load files', 'Error', 'error');
        }
      });
    }
  }

  get busy(): boolean {
    return this.saving || this.uploadingFile || this.deleteFileState.isDeleting;
  }

  closeModal() {
    if (this.busy) {
      return;
    }
    this.close.emit();
  }

  onBackdropClick() {
    this.closeModal();
  }

  saveWorkspace() {
    if (this.saving) {
      return;
    }

    if (!this.workspaceData.name?.trim()) {
      this.popupService.notify('Workspace name is required.', 'Missing name', 'warning');
      return;
    }

    this.save.emit({
      id: this.workspaceData.id,
      name: this.workspaceData.name.trim(),
      description: this.workspaceData.description?.trim() ?? '',
      instructions: this.workspaceData.instructions?.trim() ?? ''
    });
  }

  get currentTotalSize(): number {
    return (this.workspaceData.files ?? []).reduce((sum, f) => sum + (f.file_size || 0), 0);
  }

  get availableSize(): number {
    return Math.max(0, WORKSPACE_MAX_SIZE_BYTES - this.currentTotalSize);
  }

  get atLimit(): boolean {
    return this.currentTotalSize >= WORKSPACE_MAX_SIZE_BYTES;
  }

  get availableSizeLabel(): string {
    return `${(this.availableSize / MB).toFixed(1)} MB available`;
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (this.loadingFiles) {
      // Existing file list hasn't arrived yet — refuse to validate against incomplete state
      input.value = '';
      return;
    }
    if (input.files && input.files.length > 0 && this.workspace.id) {
      const file = input.files[0];

      if (file.size > this.availableSize) {
        const usedMb = (this.currentTotalSize / MB).toFixed(2);
        const fileMb = (file.size / MB).toFixed(2);
        this.popupService.notify(
          `Cannot upload "${file.name}" (${fileMb} MB). Workspace already uses ${usedMb} MB of 10 MB.`,
          'Workspace limit reached',
          'error'
        );
        input.value = '';
        return;
      }

      this.uploadingFile = true;
      this.workspaceService.uploadFile(this.workspace.id, file).subscribe({
        next: () => {
          this.uploadingFile = false;
          this.popupService.notify('File has been successfully uploaded!', 'File uploaded', 'success');
          this.loadWorkspaceFiles();
          this.filesUpdated.emit();
        },
        error: () => {
          this.uploadingFile = false;
          this.popupService.notify('Failed to upload file. Please try again.', 'Upload failed', 'error');
        }
      });

      input.value = '';
    }
  }

  removeFile(file: WorkspaceFile) {
    if (!this.workspace.id || !file.id) {
      return;
    }
    this.deleteFileState = {
      show: true,
      file,
      isDeleting: false
    };
  }

  confirmDeleteFile() {
    const file = this.deleteFileState.file;
    if (!this.workspace.id || !file) {
      return;
    }
    this.deleteFileState.isDeleting = true;
    this.workspaceService.deleteFile(this.workspace.id, file.id).subscribe({
      next: () => {
        this.deleteFileState = { show: false, file: null, isDeleting: false };
        this.popupService.notify('File has been successfully deleted!', 'File deleted', 'success');
        this.loadWorkspaceFiles();
        this.filesUpdated.emit();
      },
      error: () => {
        this.deleteFileState.isDeleting = false;
        this.popupService.notify('Failed to delete file. Please try again.', 'Delete failed', 'error');
      }
    });
  }

  cancelDeleteFile() {
    if (this.deleteFileState.isDeleting) {
      return;
    }
    this.deleteFileState = { show: false, file: null, isDeleting: false };
  }

  getFileType(file: WorkspaceFile): string {
    const extension = file.extension.toLowerCase();
    if (['pdf'].includes(extension)) return 'pdf';
    if (['xls', 'xlsx'].includes(extension)) return 'xlsx';
    if (['ppt', 'pptx'].includes(extension)) return 'pptx';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)) return 'image';
    return 'document';
  }

  getFileUpdatedAt(file: WorkspaceFile): string {
    const date = new Date(file.created_at);
    const now = new Date();

    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
      return 'just now';
    }
    if (diffMinutes < 60) {
      return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
    }
    if (diffHours < 24) {
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    }

    const sameDay = date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();
    if (sameDay) {
      return 'today';
    }

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.getFullYear() === yesterday.getFullYear() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getDate() === yesterday.getDate()) {
      return 'yesterday';
    }

    if (diffDays < 7) {
      return `${diffDays} days ago`;
    }
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
    }
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30.44);
      return months === 1 ? '1 month ago' : `${months} months ago`;
    }
    const years = Math.floor(diffDays / 365.25);
    return years === 1 ? '1 year ago' : `${years} years ago`;
  }
}
